import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext } from 'react';
import { MBTextInput } from '@shared/components/textInput';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { getComponentPlaceholder, isNumberComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';
import { LookupEditFieldContainerProps } from './EditFieldContainerProps';
import { useFieldDraft } from './useFieldDraft';

interface Props extends LookupEditFieldContainerProps {
  field: NumberField | null;
  component: API.NumberComponent | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: NumberField | null) => void;
  allowClear?: boolean;
}

export function NumberEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  showIcon,
  label,
  showLabel = true,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  // The field is the backing state; `draft` only holds in-progress text the formatter would
  // rewrite — '1.' mid-typing, or an unformatted currency amount — and yields whenever the
  // field changes from outside this input.
  const fieldValue = field?.value ?? null;
  const [draft, commitDraft] = useFieldDraft<string, number | null>(fieldValue);
  const input = draft ?? field?.editableValue ?? '';
  const colors = useContext(ColorThemeContext);
  const [hasInput, onInput, onClearInput] = useBoolean(false);
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showLocalError = !isFocused;
  const showError = (!hasInput || showLocalError) && !isEmpty(error);
  const format = isNumberComponent(component) ? component.format : (field?.format ?? 'FLOAT');

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [onFocus, setComponentFocused]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
    // pass input through storedValue parser to remove non-numeric input on blur
    const storedValue = NumberField.toStoredValue(input, format);
    commitDraft(NumberField.toEditableValue(storedValue, format), storedValue);
  }, [onBlur, setComponentBlurred, format, input, commitDraft]);

  const onChangeText = useCallback(
    (textValue: string) => {
      if (!isNumberComponent(component)) {
        return;
      }
      if (textValue === '.') {
        // not a number yet — hold it in the draft and leave the field where it is
        commitDraft('0.', fieldValue);
        return;
      }

      if (isEmpty(textValue)) {
        commitDraft(textValue, null);
        onClearInput();
        updateField?.(component.id, null);
        return;
      }

      // remove all non-numeric input, allowing number-related characters
      const cleanedInput = NumberField.removeInvalidInput(textValue);
      const numericValue = NumberField.toStoredValue(cleanedInput, format);
      if (isNil(numericValue)) {
        commitDraft(cleanedInput, fieldValue);
        return;
      }

      commitDraft(cleanedInput, numericValue);
      onInput();
      updateField?.(component.id, new NumberField(numericValue, format));
    },
    [updateField, component, format, onClearInput, onInput, commitDraft, fieldValue],
  );

  const onClear = useCallback(() => {
    onClearInput();
    commitDraft('', null);
    updateField?.(component.id, null);
  }, [updateField, component.id, commitDraft, onClearInput]);

  const isReadOnly = component.readonly || isNil(updateField);
  const hasValue = !isEmpty(input);
  const showClearButton = hasValue && !isReadOnly && allowClear;
  const storableValue = NumberField.toStoredValue(input, format);
  const formattedValue = NumberField.toFormattedValue(storableValue, format);

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? error : undefined}
      header={header}
      footer={footer}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      isReadOnly={isReadOnly}
      style={style}
      printContent={formattedValue || undefined}
    >
      <MBTextInput
        noBorder
        noPadding
        inputMode="decimal"
        keyboardType="decimal-pad"
        onBlur={isReadOnly ? undefined : handleBlur}
        onFocus={isReadOnly ? undefined : handleFocus}
        value={isFocused ? input : formattedValue}
        onChangeText={onChangeText}
        placeholder={getComponentPlaceholder(component)}
        placeholderColor={colors.foregroundInactive}
        editable={!isReadOnly}
        style={{ paddingVertical: !hasValue ? grid(1.25) : undefined }}
      />
    </EditFieldContainer>
  );
}
